// Basic unit conversion for checking whether a pantry quantity covers a
// grocery requirement. Extracted from GroceryItemActions so the shopping
// list drawer's inline pantry form can share it.

export const getUnitConversionFactor = (fromUnit: string, toUnit: string): number | null => {
  const normalizedFrom = fromUnit.toLowerCase().trim();
  const normalizedTo = toUnit.toLowerCase().trim();

  if (normalizedFrom === normalizedTo) return 1;

  // Volume conversions (to cups as base)
  const volumeConversions: Record<string, number> = {
    teaspoon: 1 / 48, tsp: 1 / 48,
    tablespoon: 1 / 16, tbsp: 1 / 16,
    'fluid ounce': 1 / 8, 'fl oz': 1 / 8,
    cup: 1, pint: 2, pt: 2, quart: 4, qt: 4, gallon: 16, gal: 16,
    dash: 1 / 384, pinch: 1 / 768,
  };

  // Weight conversions (to ounces as base)
  const weightConversions: Record<string, number> = {
    ounce: 1, oz: 1, pound: 16, lb: 16,
  };

  // Check if both units are in the same category
  if (volumeConversions[normalizedFrom] && volumeConversions[normalizedTo]) {
    return volumeConversions[normalizedFrom] / volumeConversions[normalizedTo];
  }
  if (weightConversions[normalizedFrom] && weightConversions[normalizedTo]) {
    return weightConversions[normalizedFrom] / weightConversions[normalizedTo];
  }
  return null;
};

/**
 * True when a saved pantry item satisfies a grocery requirement: staples
 * always do; ingredients do when their quantity converts to at least the
 * required amount. Unconvertible unit pairs count as not covering.
 */
export const pantryItemCoversRequirement = (
  savedItem: { type?: string; quantity?: number; unit?: string },
  requiredQuantity: number,
  requiredUnit: string
): boolean => {
  if (savedItem.type === 'staple') return true;

  const pantryQuantity = savedItem.quantity || 0;
  const conversionFactor = getUnitConversionFactor(savedItem.unit || '', requiredUnit);
  if (conversionFactor === null) return false;

  return pantryQuantity * conversionFactor >= requiredQuantity;
};
